import Dexie, { type EntityTable } from 'dexie';
import type {
  EvidenceSourceLocal,
  CardLocal,
  CardFinancialObservationLocal,
  CardAutopayObservationLocal,
  GraceConfirmationLocal,
  FieldAssertionLocal,
  RewardRuleLocal,
  BenefitLocal,
  CardFeeScheduleLocal,
  MerchantLocal,
  OfferLocal,
  StatementCycleLocal,
  PurchaseQueryLocal,
  RecommendationResultLocal,
  PaymentHistoryCoverageLocal,
  TransactionLocal,
  TransactionSplitLocal,
  CardPaymentLocal,
  PaymentAllocationLocal,
  CashReservationLocal,
  DeferredInterestExposureLocal,
  RecurringChargeLocal,
  MerchantCategoryObservationLocal,
  ReviewItemLocal,
  ImportBatchLocal,
  SettingsLocal,
} from '../schemas/entities';
import type { OutboxEntry, SyncConflict, SyncState, RewardCapPeriod } from '../schemas/local-only';

/**
 * Local Dexie replica. Synced tables carry deleted_at, client UUIDs,
 * _localDirty, _baseServerUpdatedAt from day one (ADR-016).
 */
export class CardZappDB extends Dexie {
  evidence_sources!: EntityTable<EvidenceSourceLocal, 'id'>;
  cards!: EntityTable<CardLocal, 'id'>;
  card_financial_observations!: EntityTable<CardFinancialObservationLocal, 'id'>;
  card_autopay_observations!: EntityTable<CardAutopayObservationLocal, 'id'>;
  grace_confirmations!: EntityTable<GraceConfirmationLocal, 'id'>;
  field_assertions!: EntityTable<FieldAssertionLocal, 'id'>;
  reward_rules!: EntityTable<RewardRuleLocal, 'id'>;
  benefits!: EntityTable<BenefitLocal, 'id'>;
  card_fee_schedules!: EntityTable<CardFeeScheduleLocal, 'id'>;
  merchants!: EntityTable<MerchantLocal, 'id'>;
  offers!: EntityTable<OfferLocal, 'id'>;
  statement_cycles!: EntityTable<StatementCycleLocal, 'id'>;
  purchase_queries!: EntityTable<PurchaseQueryLocal, 'id'>;
  recommendation_results!: EntityTable<RecommendationResultLocal, 'id'>;
  payment_history_coverage!: EntityTable<PaymentHistoryCoverageLocal, 'id'>;
  transactions!: EntityTable<TransactionLocal, 'id'>;
  transaction_splits!: EntityTable<TransactionSplitLocal, 'id'>;
  card_payments!: EntityTable<CardPaymentLocal, 'id'>;
  payment_allocations!: EntityTable<PaymentAllocationLocal, 'id'>;
  cash_reservations!: EntityTable<CashReservationLocal, 'id'>;
  deferred_interest_exposures!: EntityTable<DeferredInterestExposureLocal, 'id'>;
  recurring_charges!: EntityTable<RecurringChargeLocal, 'id'>;
  merchant_category_observations!: EntityTable<MerchantCategoryObservationLocal, 'id'>;
  review_items!: EntityTable<ReviewItemLocal, 'id'>;
  import_batches!: EntityTable<ImportBatchLocal, 'id'>;
  settings!: EntityTable<SettingsLocal, 'id'>;

  outbox!: EntityTable<OutboxEntry, 'id'>;
  sync_state!: EntityTable<SyncState, 'table'>;
  sync_conflicts!: EntityTable<SyncConflict, 'id'>;
  reward_cap_periods!: EntityTable<RewardCapPeriod, 'id'>;

  constructor(name = 'CardZappDB') {
    super(name);
    this.version(1).stores({
      evidence_sources: 'id, user_id, card_id, deleted_at',
      cards: 'id, user_id, deleted_at',
      card_financial_observations: 'id, user_id, card_id, observed_at, deleted_at',
      card_autopay_observations: 'id, user_id, card_id, observed_at, deleted_at',
      grace_confirmations: 'id, user_id, card_id, deleted_at',
      field_assertions: 'id, user_id, entity_id, deleted_at',
      reward_rules: 'id, user_id, card_id, deleted_at',
      benefits: 'id, user_id, card_id, deleted_at',
      card_fee_schedules: 'id, user_id, card_id, deleted_at',
      merchants: 'id, user_id, normalized_name, deleted_at',
      offers: 'id, user_id, card_id, deleted_at',
      statement_cycles: 'id, user_id, card_id, cycle_end_date, deleted_at',
      purchase_queries: 'id, user_id, deleted_at',
      recommendation_results: 'id, user_id, purchase_query_id, deleted_at',
      payment_history_coverage: 'id, user_id, card_id, deleted_at',
      transactions: 'id, user_id, card_id, posted_date, deleted_at',
      transaction_splits: 'id, user_id, transaction_id, deleted_at',
      card_payments: 'id, user_id, card_id, deleted_at',
      payment_allocations: 'id, user_id, payment_id, deleted_at',
      cash_reservations: 'id, user_id, card_id, deleted_at',
      deferred_interest_exposures: 'id, user_id, card_id, deleted_at',
      recurring_charges: 'id, user_id, deleted_at',
      merchant_category_observations: 'id, user_id, merchant_id, deleted_at',
      review_items: 'id, user_id, card_id, deleted_at',
      import_batches: 'id, user_id, deleted_at',
      settings: 'id, user_id, deleted_at',
      outbox: 'id, table, rowId, createdAt',
      sync_state: 'table',
      sync_conflicts: 'id, table, rowId',
      reward_cap_periods: 'id',
    });
  }
}

export function openCardZappDb(name?: string): CardZappDB {
  return new CardZappDB(name);
}
